import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { refreshMessageAttachmentUrls } from '../services/teamChatUploadService';
import { userProfileCache } from './referenceDataCache';
import { replyToTaskComment } from '../services/taskCommentBridgeService';

// LRU Cache configuration for messages
const MAX_CACHED_CHATS = 15;
const MESSAGES_PAGE_SIZE = 50;

// Mappa tempId → realId per evitare duplicati quando il realtime arriva prima/dopo la conferma
const resolvedTempIds = new Set<string>();

// Notifiche push team chat — DISABILITATO lato client (3 lug 2026).
// Ora sono inviate LATO SERVER da un trigger DB su team_chat_messages
// (migration 20260703_team_chat_push_server_trigger.sql), che chiama
// create-notification per i partecipanti non-mittente e non-mutati. Il trigger
// client-side fire-and-forget era inaffidabile (push mai arrivati); mantenerlo
// attivo genererebbe push DOPPI. Stub no-op per non toccare i call site.
// TODO: reintrodurre la priorità alta per le @menzioni a livello di trigger
// (oggi il server invia sempre team_chat_new_message).
const sendTeamChatNotification = (
  _chat: TeamChat,
  _senderId: string,
  _senderName: string,
  _content: string,
  _messageId: string,
  _excludeFromGeneric: string[] = []
): void => {
  /* no-op: gestito server-side */
};

// Helper: query messaggi con select standard (reactions incluse)
const buildMessagesQuery = (chatId: string, withReactions = true) => {
  const selectStr = withReactions
    ? `*, sender:user_profiles!sender_id(id, full_name, profile_image_url, avatar_url), reply_to:team_chat_messages!reply_to_id(id, content, sender:user_profiles!sender_id(id, full_name)), reactions:team_chat_message_reactions(emoji, user_id)`
    : `*, sender:user_profiles!sender_id(id, full_name, profile_image_url, avatar_url), reply_to:team_chat_messages!reply_to_id(id, content, sender:user_profiles!sender_id(id, full_name))`;
  return supabase
    .from('team_chat_messages')
    .select(selectStr)
    .eq('chat_id', chatId)
    .eq('is_deleted', false);
};

// Helper: normalizza sender + raggruppa reazioni
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeMessages = (data: any[]): TeamChatMessage[] =>
  data.map((msg: any) => {
    if (msg.sender) msg.sender.profile_image_url = msg.sender.avatar_url || msg.sender.profile_image_url || null;
    const rawReactions: { emoji: string; user_id: string }[] = msg.reactions || [];
    const reactionMap = new Map<string, string[]>();
    for (const r of rawReactions) {
      const users = reactionMap.get(r.emoji) || [];
      users.push(r.user_id);
      reactionMap.set(r.emoji, users);
    }
    msg.reactions = Array.from(reactionMap.entries()).map(([emoji, user_ids]) => ({ emoji, user_ids, count: user_ids.length }));
    return msg;
  });

// Normalizza immagine: admin salva su avatar_url, utente su profile_image_url
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeParticipants = (participants: any[]) =>
  participants.map(p => ({
    ...p,
    user: p.user ? {
      ...p.user,
      profile_image_url: p.user.avatar_url || p.user.profile_image_url || null
    } : p.user
  }));

// Types
export interface TeamChatVisitor {
  id: string;
  visitor_name: string | null;
  visitor_email: string | null;
  last_seen_at: string;
  lead_id: string | null;
  lead_form_id: string;
}

export interface TeamChat {
  id: string;
  organization_id: string;
  name: string | null;
  is_group: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  chat_type?: 'standard' | 'visitor';
  // Joined data
  participants?: TeamChatParticipant[];
  last_message?: TeamChatMessage | null;
  unread_count?: number;
  has_unread_mention?: boolean; // client-only: true se ci sono menzioni non lette
  visitor?: TeamChatVisitor | null; // popolato solo quando chat_type='visitor'
}

export interface TeamChatParticipant {
  id: string;
  chat_id: string;
  user_id: string;
  joined_at: string;
  last_read_at: string;
  // Joined data
  user?: {
    id: string;
    full_name: string;
    profile_image_url?: string | null; // normalizzato: avatar_url || profile_image_url
  };
}

export type MessageAttachmentType = 'image' | 'file' | 'video' | 'audio';

export interface MessageAttachment {
  type: MessageAttachmentType;
  url: string;            // signed URL (temporaneo, usato a runtime)
  storage_path?: string;  // path relativo nel bucket (persistito nel DB)
  storage_bucket?: string; // nome bucket (persistito nel DB)
  name: string;
  size?: number;
  mime_type?: string;
  duration?: number; // secondi (audio/video)
  transcription?: string; // testo trascritto (audio)
}

export interface MessageReaction {
  emoji: string;
  user_ids: string[];
  count: number;
}

export interface TeamChatMessage {
  id: string;
  chat_id: string;
  sender_id: string | null;
  content: string;
  message_type?: string; // 'text' | 'audio' | 'image' | 'file' | 'video' | 'whatsapp_forward' | 'task_comment'
  attachments?: MessageAttachment[];
  mentions?: string[]; // user_ids taggati con @
  reply_to_id?: string | null;
  is_edited: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  metadata?: {
    source_table?: string;
    source_comment_id?: string;
    task_id?: string;
    project_id?: string;
    [key: string]: unknown;
  } | null;
  // Joined data
  sender?: {
    id: string;
    full_name: string;
    profile_image_url?: string | null;
  } | null;
  reply_to?: {
    id: string;
    content: string;
    sender?: {
      id: string;
      full_name: string;
    };
  } | null;
  reactions?: MessageReaction[];
  is_pinned?: boolean;
  read_by?: string[];
  forwarded_from_id?: string | null;
  thread_message_count?: number;
  thread_last_reply_at?: string | null;
  // Optimistic send status (client-only, not persisted)
  _status?: 'sending' | 'failed';
}

type WidgetState = 'minimized' | 'list' | 'chat' | 'expanded';

export type TypingUser = { userId: string; userName: string };

export interface ScheduledMessage {
  id: string;
  chat_id: string;
  sender_id: string;
  content: string;
  message_type: string;
  attachments: MessageAttachment[];
  reply_to_id?: string | null;
  scheduled_at: string;
  status: 'scheduled' | 'sent' | 'cancelled';
  created_at: string;
}

interface TeamChatStore {
  // State
  chats: TeamChat[];
  archivedChats: TeamChat[];
  selectedChatId: string | null;
  messages: Record<string, TeamChatMessage[]>; // chatId -> messages
  messagesCacheOrder: string[]; // LRU cache order (most recently used at end)
  hasMoreMessages: Record<string, boolean>; // chatId -> true se ci sono messaggi piu vecchi
  loadingOlderMessages: boolean;
  loading: boolean;
  loadingMessages: boolean;
  error: string | null;
  widgetState: WidgetState;
  replyingTo: TeamChatMessage | null;
  organizationUsers: { id: string; full_name: string; profile_image_url?: string | null }[];
  typingUsers: Record<string, TypingUser[]>; // chatId -> utenti che stanno scrivendo

  // Computed
  totalUnreadCount: () => number;
  selectedChat: () => TeamChat | null;
  selectedChatMessages: () => TeamChatMessage[];

  // Actions
  fetchChats: () => Promise<void>;
  fetchArchivedChats: () => Promise<void>;
  fetchMessages: (chatId: string) => Promise<void>;
  fetchOlderMessages: (chatId: string) => Promise<void>;
  fetchOrganizationUsers: () => Promise<void>;
  createChat: (userIds: string[], name?: string) => Promise<TeamChat | null>;
  sendMessage: (chatId: string, content: string, replyToId?: string | null, attachments?: MessageAttachment[], messageType?: string) => Promise<void>;
  retryMessage: (tempId: string, chatId: string) => Promise<void>;
  discardFailedMessage: (tempId: string, chatId: string) => void;
  editMessage: (messageId: string, newContent: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  markChatAsRead: (chatId: string) => Promise<void>;
  markChatAsUnread: (chatId: string) => void;
  selectChat: (chatId: string | null) => Promise<void>;
  leaveChat: (chatId: string) => Promise<void>;
  renameChat: (chatId: string, name: string) => Promise<void>;
  archiveChat: (chatId: string) => Promise<void>;
  restoreChat: (chatId: string) => Promise<void>;
  addParticipants: (chatId: string, userIds: string[]) => Promise<void>;
  removeParticipant: (chatId: string, userId: string) => Promise<void>;
  setUserTyping: (chatId: string, userId: string, userName: string) => void;
  clearTyping: (chatId: string, userId: string) => void;
  setWidgetState: (state: WidgetState) => void;
  setReplyingTo: (message: TeamChatMessage | null) => void;
  addReaction: (messageId: string, chatId: string, emoji: string) => Promise<void>;
  removeReaction: (messageId: string, chatId: string, emoji: string) => Promise<void>;

  // Enhanced features
  searchMessages: (chatId: string, query: string) => Promise<{ id: string; content: string; sender_name: string; created_at: string }[]>;
  searchAllChats: (query: string) => Promise<{ id: string; chat_id: string; content: string; sender_name: string; created_at: string; chat_name: string }[]>;
  forwardMessage: (messageId: string, fromChatId: string, toChatId: string) => Promise<void>;
  togglePinMessage: (messageId: string, chatId: string) => Promise<void>;
  getPinnedMessages: (chatId: string) => Promise<TeamChatMessage[]>;
  toggleMuteChat: (chatId: string) => Promise<void>;
  markMessageAsRead: (messageId: string, chatId: string) => Promise<void>;
  getMessageReadBy: (messageId: string) => Promise<{ user_id: string; full_name: string; profile_image_url?: string | null }[]>;

  // Saved messages
  savedMessageIds: Set<string>;
  saveMessage: (messageId: string, chatId: string) => Promise<void>;
  unsaveMessage: (messageId: string) => Promise<void>;
  fetchSavedMessageIds: () => Promise<void>;
  getSavedMessages: () => Promise<(TeamChatMessage & { chat_name?: string })[]>;

  // Scheduled messages
  scheduledMessages: ScheduledMessage[];
  scheduleMessage: (chatId: string, content: string, scheduledAt: Date, replyToId?: string | null, attachments?: MessageAttachment[], messageType?: string) => Promise<void>;
  getScheduledMessages: (chatId?: string) => Promise<ScheduledMessage[]>;
  cancelScheduledMessage: (messageId: string) => Promise<void>;

  // Threads
  fetchThreadMessages: (chatId: string, rootMessageId: string) => Promise<TeamChatMessage[]>;
  sendThreadMessage: (chatId: string, rootMessageId: string, content: string, attachments?: MessageAttachment[], messageType?: string) => Promise<void>;

  // Realtime handlers
  addChatFromRealtime: (chat: TeamChat) => void;
  addMessageFromRealtime: (message: TeamChatMessage) => void;
  updateMessageFromRealtime: (message: TeamChatMessage) => void;
  addReactionFromRealtime: (messageId: string, chatId: string, emoji: string, userId: string) => void;
  removeReactionFromRealtime: (messageId: string, chatId: string, emoji: string, userId: string) => void;

  reset: () => void;
}

// Suono notifica messaggi ricevuti
function playNotificationSound() {
  try {
    type WindowWithWebkit = Window & { webkitAudioContext?: typeof AudioContext };
    const AudioCtx = window.AudioContext || (window as WindowWithWebkit).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(659.25, ctx.currentTime);      // E5
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.12);  // A5
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.45);
  } catch { /* Audio API non disponibile o bloccata dal browser */ }
}

// Timeout IDs per il typing indicator (fuori dallo store per evitare serializzazione)
const typingTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export const useTeamChatStore = create<TeamChatStore>()(
  (set, get) => ({
      chats: [],
      archivedChats: [],
      selectedChatId: null,
      messages: {},
      messagesCacheOrder: [],
      hasMoreMessages: {},
      loadingOlderMessages: false,
      loading: false,
      loadingMessages: false,
      error: null,
      widgetState: 'minimized',
      replyingTo: null,
      organizationUsers: [],
      typingUsers: {},
      savedMessageIds: new Set<string>(),
      scheduledMessages: [],

      // Computed
      totalUnreadCount: () => {
        return get().chats.reduce((acc, chat) => acc + (chat.unread_count || 0), 0);
      },

      selectedChat: () => {
        const { chats, selectedChatId } = get();
        return chats.find(c => c.id === selectedChatId) || null;
      },

      selectedChatMessages: () => {
        const { messages, selectedChatId } = get();
        return selectedChatId ? messages[selectedChatId] || [] : [];
      },

      fetchChats: async () => {
        set({ loading: true, error: null });
        try {
          const { data: userData } = await supabase.auth.getUser();
          if (!userData.user) throw new Error('Not authenticated');
          const currentUserId = userData.user.id;

          // Query 1: Get my participant records with last_read_at
          const { data: myParticipants, error: pError } = await supabase
            .from('team_chat_participants')
            .select('chat_id, last_read_at')
            .eq('user_id', currentUserId);

          if (pError) throw pError;
          if (!myParticipants || myParticipants.length === 0) {
            set({ chats: [], loading: false });
            return;
          }

          const chatIds = myParticipants.map(p => p.chat_id);
          const lastReadMap = new Map(myParticipants.map(p => [p.chat_id, p.last_read_at]));

          // Query 2: Get chat details with participants (solo non archiviate)
          // Include relation `chat_visitors` per le visitor chat (1:1 lato logico, ma Supabase la ritorna come array)
          const { data: chatsData, error: cError } = await supabase
            .from('team_chats')
            .select(`
              *,
              participants:team_chat_participants(
                id,
                user_id,
                last_read_at,
                user:user_profiles(id, full_name, profile_image_url, avatar_url)
              ),
              chat_visitor:chat_visitors(
                id,
                visitor_name,
                visitor_email,
                last_seen_at,
                lead_id,
                lead_form_id
              )
            `)
            .in('id', chatIds)
            .eq('is_archived', false);

          if (cError) throw cError;

          // Query 3: Get recent messages for all chats in ONE query (for last_message + unread count)
          const { data: recentMessages, error: mError } = await supabase
            .from('team_chat_messages')
            .select(`
              id, chat_id, sender_id, content, message_type, attachments, created_at, is_edited,
              sender:user_profiles!sender_id(id, full_name, profile_image_url, avatar_url)
            `)
            .in('chat_id', chatIds)
            .eq('is_deleted', false)
            .order('created_at', { ascending: false });

          if (mError) throw mError;

          // Build last_message map and count unread messages (client-side processing)
          const lastMessageMap = new Map<string, TeamChatMessage>();
          const unreadCountMap = new Map<string, number>();

          // Initialize unread counts to 0
          chatIds.forEach(id => unreadCountMap.set(id, 0));

          for (const msg of recentMessages || []) {
            // Set last message if not already set for this chat (first = most recent due to ORDER BY)
            if (!lastMessageMap.has(msg.chat_id)) {
              // Normalizza immagine sender: avatar_url (admin) ha priorità su profile_image_url (self)
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const s = (msg as any).sender;
              if (s) s.profile_image_url = s.avatar_url || s.profile_image_url || null;
              lastMessageMap.set(msg.chat_id, msg as unknown as TeamChatMessage);
            }

            // Count unread messages (not from me, after my last_read_at)
            const lastReadAt = lastReadMap.get(msg.chat_id);
            if (lastReadAt && msg.sender_id !== currentUserId && new Date(msg.created_at) > new Date(lastReadAt)) {
              unreadCountMap.set(msg.chat_id, (unreadCountMap.get(msg.chat_id) || 0) + 1);
            }
          }

          // Enrich chats with last_message, unread_count e visitor normalizzato (1:1)
          const enrichedChats = (chatsData || []).map(chat => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rawVisitor = (chat as any).chat_visitor;
            const visitor: TeamChatVisitor | null = Array.isArray(rawVisitor)
              ? (rawVisitor[0] ?? null)
              : (rawVisitor ?? null);
            return {
              ...chat,
              participants: normalizeParticipants(chat.participants || []),
              last_message: lastMessageMap.get(chat.id) || null,
              unread_count: unreadCountMap.get(chat.id) || 0,
              visitor,
            };
          });

          // Sort by last message time (most recent first)
          enrichedChats.sort((a, b) => {
            const aTime = a.last_message?.created_at || a.created_at;
            const bTime = b.last_message?.created_at || b.created_at;
            return new Date(bTime).getTime() - new Date(aTime).getTime();
          });

          set({ chats: enrichedChats, loading: false });
        } catch (error) {
          console.error('[TeamChatStore] Error fetching chats:', error);
          set({ error: friendlyDbError(error), loading: false });
        }
      },

      fetchMessages: async (chatId: string) => {
        set({ loadingMessages: true });
        try {
          // Carica gli ultimi MESSAGES_PAGE_SIZE messaggi (desc + reverse per avere ordine cronologico)
          let result = await buildMessagesQuery(chatId)
            .order('created_at', { ascending: false })
            .limit(MESSAGES_PAGE_SIZE);

          if (result.error && result.error.code === '42P01') {
            result = await buildMessagesQuery(chatId, false)
              .order('created_at', { ascending: false })
              .limit(MESSAGES_PAGE_SIZE);
          }

          const { data, error } = result;
          if (error) throw error;

          // Reverse per ordine cronologico (dal più vecchio al più recente)
          const normalizedData = normalizeMessages((data || []).reverse());
          const hasMore = (data || []).length >= MESSAGES_PAGE_SIZE;

          set(state => {
            let newMessages = { ...state.messages, [chatId]: normalizedData };
            let newCacheOrder = state.messagesCacheOrder.filter(id => id !== chatId);
            newCacheOrder.push(chatId);

            while (newCacheOrder.length > MAX_CACHED_CHATS) {
              const oldestChatId = newCacheOrder.shift();
              if (oldestChatId && oldestChatId !== chatId) {
                delete newMessages[oldestChatId];
              }
            }

            return {
              messages: newMessages,
              messagesCacheOrder: newCacheOrder,
              hasMoreMessages: { ...state.hasMoreMessages, [chatId]: hasMore },
              loadingMessages: false
            };
          });

          // Rigenera signed URL in background per attachment con storage_path
          const hasAttachments = normalizedData.some(m => m.attachments?.some(a => a.storage_path));
          if (hasAttachments) {
            refreshMessageAttachmentUrls(normalizedData).then(refreshed => {
              if (get().selectedChatId !== chatId) return; // chat cambiata, ignora
              set(state => ({
                messages: { ...state.messages, [chatId]: refreshed }
              }));
            }).catch(() => { /* silent — URLs vecchi funzionano ancora se non scaduti */ });
          }
        } catch (error) {
          console.error('[TeamChatStore] Error fetching messages:', error);
          set({ loadingMessages: false });
        }
      },

      fetchOlderMessages: async (chatId: string) => {
        const state = get();
        if (state.loadingOlderMessages) return;
        if (!state.hasMoreMessages[chatId]) return;

        const existingMessages = state.messages[chatId] || [];
        if (!existingMessages.length) return;

        const oldestMessage = existingMessages[0];
        set({ loadingOlderMessages: true });

        try {
          let result = await buildMessagesQuery(chatId)
            .lt('created_at', oldestMessage.created_at)
            .order('created_at', { ascending: false })
            .limit(MESSAGES_PAGE_SIZE);

          if (result.error && result.error.code === '42P01') {
            result = await buildMessagesQuery(chatId, false)
              .lt('created_at', oldestMessage.created_at)
              .order('created_at', { ascending: false })
              .limit(MESSAGES_PAGE_SIZE);
          }

          const { data, error } = result;
          if (error) throw error;

          const olderMessages = normalizeMessages((data || []).reverse());
          const hasMore = (data || []).length >= MESSAGES_PAGE_SIZE;

          set(state => ({
            messages: {
              ...state.messages,
              [chatId]: [...olderMessages, ...(state.messages[chatId] || [])],
            },
            hasMoreMessages: { ...state.hasMoreMessages, [chatId]: hasMore },
            loadingOlderMessages: false,
          }));
        } catch (error) {
          console.error('[TeamChatStore] Error fetching older messages:', error);
          set({ loadingOlderMessages: false });
        }
      },

      fetchOrganizationUsers: async () => {
        try {
          const { data: userData } = await supabase.auth.getUser();
          if (!userData.user) return;

          const organizationId = await userProfileCache.getOrganizationId(userData.user.id).catch(() => null);
          if (!organizationId) return;

          const { data: users } = await supabase
            .from('user_profiles')
            .select('id, full_name, profile_image_url, avatar_url')
            .eq('organization_id', organizationId)
            .eq('is_super_admin', false) // Escludi superadmin
            .eq('active', true) // Escludi utenti disattivati
            .neq('id', userData.user.id)
            .order('full_name');

          set({
            organizationUsers: (users || []).map(u => ({
              ...u,
              profile_image_url: (u as unknown as { avatar_url?: string | null }).avatar_url || u.profile_image_url || null
            }))
          });
        } catch (error) {
          console.error('[TeamChatStore] Error fetching users:', error);
        }
      },

      createChat: async (userIds: string[], name?: string) => {
        try {
          const { data: userData } = await supabase.auth.getUser();
          if (!userData.user) throw new Error('Not authenticated');

          const { data: profile } = await supabase
            .from('user_profiles')
            .select('organization_id')
            .eq('id', userData.user.id)
            .single();

          if (!profile?.organization_id) throw new Error('No organization');

          const isGroup = userIds.length > 1 || !!name;

          // Check if 1-to-1 chat already exists
          if (!isGroup && userIds.length === 1) {
            const { chats } = get();
            const existingChat = chats.find(chat => {
              if (chat.is_group) return false;
              const participantIds = chat.participants?.map(p => p.user_id) || [];
              return participantIds.includes(userIds[0]) && participantIds.includes(userData.user!.id);
            });

            if (existingChat) {
              set({ selectedChatId: existingChat.id, widgetState: 'expanded' });
              return existingChat;
            }
          }

          // Create new chat
          const { data: newChat, error: chatError } = await supabase
            .from('team_chats')
            .insert({
              organization_id: profile.organization_id,
              name: isGroup ? name : null,
              is_group: isGroup,
              created_by: userData.user.id
            })
            .select()
            .single();

          if (chatError) throw chatError;

          // Add participants (including self)
          const allUserIds = [userData.user.id, ...userIds];
          const { error: participantsError } = await supabase
            .from('team_chat_participants')
            .insert(allUserIds.map(userId => ({
              chat_id: newChat.id,
              user_id: userId
            })));

          if (participantsError) throw participantsError;

          // Fetch and update chats
          await get().fetchChats();

          // Verifica che la chat sia nella lista, altrimenti aggiungila manualmente
          const { chats, organizationUsers } = get();
          if (!chats.find(c => c.id === newChat.id)) {
            // Build participants with user data from organizationUsers
            const chatWithParticipants = {
              ...newChat,
              participants: allUserIds.map(uid => {
                // Find user in organizationUsers, or use current user data
                const userFromOrg = organizationUsers.find(u => u.id === uid);
                return {
                  id: crypto.randomUUID(),
                  chat_id: newChat.id,
                  user_id: uid,
                  joined_at: new Date().toISOString(),
                  last_read_at: new Date().toISOString(),
                  user: userFromOrg ? {
                    id: userFromOrg.id,
                    full_name: userFromOrg.full_name,
                    profile_image_url: userFromOrg.profile_image_url
                  } : null
                };
              }),
              last_message: null,
              unread_count: 0
            };
            set(state => ({ chats: [chatWithParticipants, ...state.chats] }));
          }

          set({ selectedChatId: newChat.id, widgetState: 'expanded' });
          return newChat;
        } catch (error) {
          console.error('[TeamChatStore] Error creating chat:', error);
          set({ error: friendlyDbError(error) });
          return null;
        }
      },

      sendMessage: async (chatId: string, content: string, replyToId?: string | null, attachments?: MessageAttachment[], messageType?: string) => {
        if (content.length > 5000) throw new Error('Il messaggio è troppo lungo (max 5000 caratteri)');

        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error('Not authenticated');

        // 1. Crea messaggio ottimistico con ID temporaneo
        const tempId = `temp_${crypto.randomUUID()}`;
        const userProfile = get().organizationUsers.find(u => u.id === userData.user!.id);

        // Arricchisci con reply_to dalla cache locale
        let replyTo: TeamChatMessage['reply_to'] = null;
        if (replyToId) {
          const cached = (get().messages[chatId] || []).find(m => m.id === replyToId);
          if (cached) {
            replyTo = { id: cached.id, content: cached.content, sender: cached.sender };
          }
        }

        const optimisticMsg: TeamChatMessage = {
          id: tempId,
          chat_id: chatId,
          sender_id: userData.user.id,
          content,
          message_type: messageType || 'text',
          attachments: attachments || [],
          reply_to_id: replyToId || null,
          reply_to: replyTo,
          is_edited: false,
          is_deleted: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          sender: userProfile ? { id: userProfile.id, full_name: userProfile.full_name, profile_image_url: userProfile.profile_image_url } : null,
          _status: 'sending',
        };

        // 2. Aggiungi subito allo stato locale
        set(state => ({
          messages: {
            ...state.messages,
            [chatId]: [...(state.messages[chatId] || []), optimisticMsg]
          },
          replyingTo: null,
          chats: state.chats.map(chat =>
            chat.id === chatId ? { ...chat, last_message: optimisticMsg } : chat
          )
        }));

        // 3. Insert su Supabase (async — non blocca l'UI)
        try {
          const { data: newMsg, error } = await supabase
            .from('team_chat_messages')
            .insert({
              chat_id: chatId,
              sender_id: userData.user.id,
              content,
              reply_to_id: replyToId || null,
              message_type: messageType || 'text',
              attachments: attachments || []
            })
            .select('*, sender:user_profiles!sender_id(id, full_name, profile_image_url, avatar_url)')
            .single();

          if (error) throw error;

          // 4. Sostituisci il messaggio temp con quello reale dal server
          if (newMsg) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const s = (newMsg as any).sender;
            if (s) s.profile_image_url = s.avatar_url || s.profile_image_url || null;
            let enrichedMsg = newMsg as unknown as TeamChatMessage;
            if (replyTo) enrichedMsg = { ...enrichedMsg, reply_to: replyTo };

            // Registra l'ID reale così il realtime handler lo skippa se arriva dopo
            resolvedTempIds.add(newMsg.id);
            // Cleanup dopo 30s per evitare memory leak
            setTimeout(() => resolvedTempIds.delete(newMsg.id), 30000);

            set(state => ({
              messages: {
                ...state.messages,
                [chatId]: (state.messages[chatId] || []).map(m =>
                  m.id === tempId ? enrichedMsg : m
                )
              },
              chats: state.chats.map(chat =>
                chat.id === chatId ? { ...chat, last_message: enrichedMsg } : chat
              )
            }));

            // Notifiche push team chat: gestite LATO SERVER da un trigger DB su
            // team_chat_messages (migration 20260703). Le invocazioni client
            // (generico/menzione/reply) sono disabilitate per evitare push doppi.
            // Vedi sendTeamChatNotification (no-op) sopra.
            void sendTeamChatNotification;

            // Reply-back: se rispondiamo a un task_comment
            if (replyToId) {
              const parentMsg = (get().messages[chatId] || []).find(m => m.id === replyToId);
              if (parentMsg?.message_type === 'task_comment' && parentMsg.metadata?.source_table) {
                const sName = (newMsg as any).sender?.full_name || 'Staff';
                replyToTaskComment(parentMsg, content, userData.user!.id, sName)
                  .catch(err => console.error('[TeamChat] Task comment reply-back error:', err));
              }
            }
          }
        } catch (error) {
          console.error('[TeamChatStore] Error sending message:', error);
          // 5. Marca il messaggio come fallito
          set(state => ({
            messages: {
              ...state.messages,
              [chatId]: (state.messages[chatId] || []).map(m =>
                m.id === tempId ? { ...m, _status: 'failed' as const } : m
              )
            }
          }));
          throw error;
        }
      },

      retryMessage: async (tempId: string, chatId: string) => {
        const msg = (get().messages[chatId] || []).find(m => m.id === tempId);
        if (!msg || msg._status !== 'failed') return;

        // Rimuovi il messaggio fallito e reinvia
        set(state => ({
          messages: {
            ...state.messages,
            [chatId]: (state.messages[chatId] || []).filter(m => m.id !== tempId)
          }
        }));
        await get().sendMessage(chatId, msg.content, msg.reply_to_id, msg.attachments, msg.message_type);
      },

      discardFailedMessage: (tempId: string, chatId: string) => {
        set(state => ({
          messages: {
            ...state.messages,
            [chatId]: (state.messages[chatId] || []).filter(m => m.id !== tempId)
          }
        }));
      },

      editMessage: async (messageId: string, newContent: string) => {
        try {
          const { error } = await supabase
            .from('team_chat_messages')
            .update({ content: newContent, is_edited: true })
            .eq('id', messageId);

          if (error) throw error;
        } catch (error) {
          console.error('[TeamChatStore] Error editing message:', error);
          throw error;
        }
      },

      deleteMessage: async (messageId: string) => {
        try {
          // Trova il chatId dalla cache PRIMA dell'await (evita race condition con selectedChatId)
          const { messages } = get();
          const chatId = Object.keys(messages).find(cId =>
            messages[cId].some(m => m.id === messageId)
          );

          const { error } = await supabase
            .from('team_chat_messages')
            .update({ is_deleted: true })
            .eq('id', messageId);

          if (error) throw error;

          // Rimuovi dallo stato locale usando il chatId catturato prima dell'await
          if (chatId) {
            set(state => ({
              messages: {
                ...state.messages,
                [chatId]: (state.messages[chatId] || []).filter(m => m.id !== messageId)
              }
            }));
          }
        } catch (error) {
          console.error('[TeamChatStore] Error deleting message:', error);
          throw error;
        }
      },

      markChatAsRead: async (chatId: string) => {
        try {
          const { data: userData } = await supabase.auth.getUser();
          if (!userData.user) return;

          const now = new Date().toISOString();

          await supabase
            .from('team_chat_participants')
            .update({ last_read_at: now })
            .eq('chat_id', chatId)
            .eq('user_id', userData.user.id);

          // Update local state
          set(state => ({
            chats: state.chats.map(chat =>
              chat.id === chatId ? { ...chat, unread_count: 0, has_unread_mention: false } : chat
            )
          }));
        } catch (error) {
          console.error('[TeamChatStore] Error marking as read:', error);
        }
      },

      markChatAsUnread: (chatId: string) => {
        set(state => ({
          chats: state.chats.map(c =>
            c.id === chatId ? { ...c, unread_count: Math.max(c.unread_count || 0, 1) } : c
          )
        }));
      },

      selectChat: async (chatId: string | null) => {
        set({ selectedChatId: chatId, replyingTo: null });
        if (chatId) {
          // Run both operations in parallel
          await Promise.all([
            get().fetchMessages(chatId),
            get().markChatAsRead(chatId)
          ]);
          // Verify that the user hasn't switched to another chat during loading
          if (get().selectedChatId !== chatId) {
            console.log('[TeamChatStore] Chat changed during load, ignoring results for', chatId);
            return;
          }
          // Mark visible messages as read (fire-and-forget, no await)
          const { data: userData } = await supabase.auth.getUser();
          if (userData.user) {
            const msgs = get().messages[chatId] || [];
            const userId = userData.user.id;
            // Mark last 20 messages from others that haven't been read
            const toMark = msgs
              .filter(m => m.sender_id && m.sender_id !== userId && !(m.read_by || []).includes(userId))
              .slice(-20);
            for (const m of toMark) {
              get().markMessageAsRead(m.id, chatId);
            }
          }
        }
      },

      setWidgetState: (state: WidgetState) => {
        set({ widgetState: state });
        if (state === 'minimized') {
          set({ selectedChatId: null, replyingTo: null });
        }
      },

      setReplyingTo: (message: TeamChatMessage | null) => {
        set({ replyingTo: message });
      },

      addReaction: async (messageId: string, chatId: string, emoji: string) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;
        const userId = userData.user.id;

        // Optimistic update
        set(state => ({
          messages: {
            ...state.messages,
            [chatId]: (state.messages[chatId] || []).map(m => {
              if (m.id !== messageId) return m;
              const reactions = [...(m.reactions || [])];
              const existing = reactions.find(r => r.emoji === emoji);
              if (existing) {
                if (existing.user_ids.includes(userId)) return m;
                return { ...m, reactions: reactions.map(r => r.emoji === emoji ? { ...r, user_ids: [...r.user_ids, userId], count: r.count + 1 } : r) };
              }
              return { ...m, reactions: [...reactions, { emoji, user_ids: [userId], count: 1 }] };
            })
          }
        }));

        await supabase.from('team_chat_message_reactions')
          .upsert({ message_id: messageId, user_id: userId, emoji }, { onConflict: 'message_id,user_id,emoji' });
      },

      removeReaction: async (messageId: string, chatId: string, emoji: string) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;
        const userId = userData.user.id;

        // Optimistic update
        set(state => ({
          messages: {
            ...state.messages,
            [chatId]: (state.messages[chatId] || []).map(m => {
              if (m.id !== messageId) return m;
              const reactions = (m.reactions || [])
                .map(r => {
                  if (r.emoji !== emoji) return r;
                  const newIds = r.user_ids.filter(id => id !== userId);
                  return { ...r, user_ids: newIds, count: newIds.length };
                })
                .filter(r => r.count > 0);
              return { ...m, reactions };
            })
          }
        }));

        await supabase.from('team_chat_message_reactions')
          .delete()
          .match({ message_id: messageId, user_id: userId, emoji });
      },

      addReactionFromRealtime: (messageId: string, chatId: string, emoji: string, userId: string) => {
        set(state => ({
          messages: {
            ...state.messages,
            [chatId]: (state.messages[chatId] || []).map(m => {
              if (m.id !== messageId) return m;
              const reactions = [...(m.reactions || [])];
              const existing = reactions.find(r => r.emoji === emoji);
              if (existing && !existing.user_ids.includes(userId)) {
                return { ...m, reactions: reactions.map(r => r.emoji === emoji ? { ...r, user_ids: [...r.user_ids, userId], count: r.count + 1 } : r) };
              } else if (!existing) {
                return { ...m, reactions: [...reactions, { emoji, user_ids: [userId], count: 1 }] };
              }
              return m;
            })
          }
        }));
      },

      removeReactionFromRealtime: (messageId: string, chatId: string, emoji: string, userId: string) => {
        set(state => ({
          messages: {
            ...state.messages,
            [chatId]: (state.messages[chatId] || []).map(m => {
              if (m.id !== messageId) return m;
              const reactions = (m.reactions || [])
                .map(r => {
                  if (r.emoji !== emoji) return r;
                  const newIds = r.user_ids.filter(id => id !== userId);
                  return { ...r, user_ids: newIds, count: newIds.length };
                })
                .filter(r => r.count > 0);
              return { ...m, reactions };
            })
          }
        }));
      },

      // Realtime handlers
      addChatFromRealtime: (chat: TeamChat) => {
        const { chats } = get();
        if (chats.some(c => c.id === chat.id)) return;
        set({ chats: [chat, ...chats] });
      },

      addMessageFromRealtime: async (message: TeamChatMessage) => {
        const chatMessages = get().messages[message.chat_id] || [];

        // Controlla duplicati per ID reale (sendMessage ha già sostituito il temp)
        if (chatMessages.some(m => m.id === message.id)) return;

        // Se sendMessage ha già confermato questo messaggio, il temp è già stato sostituito
        if (resolvedTempIds.has(message.id)) return;

        // Se il realtime arriva PRIMA che sendMessage confermi,
        // cerca il temp message più vecchio di questo sender per sostituirlo
        const matchingTemp = chatMessages.find(m =>
          m.id.startsWith('temp_') &&
          m._status === 'sending' &&
          m.sender_id === message.sender_id &&
          m.content === message.content
        );

        // Arricchisci con sender e reply_to se mancanti
        let enrichedMessage = message;

        // Fetch sender
        if (!message.sender) {
          const { data: senderData } = await supabase
            .from('user_profiles')
            .select('id, full_name, profile_image_url, avatar_url')
            .eq('id', message.sender_id)
            .single();

          if (senderData) {
            enrichedMessage = { ...enrichedMessage, sender: senderData };
          }
        }

        // Fetch reply_to se il messaggio è una risposta ma mancano i dati della citazione
        if (message.reply_to_id && !message.reply_to) {
          // Prima controlla la cache locale
          const cached = (get().messages[message.chat_id] || []).find(m => m.id === message.reply_to_id);
          if (cached) {
            enrichedMessage = { ...enrichedMessage, reply_to: { id: cached.id, content: cached.content, sender: cached.sender } };
          } else {
            const { data: replyData } = await supabase
              .from('team_chat_messages')
              .select('id, content, sender:user_profiles!sender_id(id, full_name)')
              .eq('id', message.reply_to_id)
              .single();
            if (replyData) {
              enrichedMessage = { ...enrichedMessage, reply_to: replyData as TeamChatMessage['reply_to'] };
            }
          }
        }

        if (matchingTemp) {
          // Sostituisci il messaggio temp con quello reale dal realtime
          set(state => ({
            messages: {
              ...state.messages,
              [message.chat_id]: (state.messages[message.chat_id] || []).map(m =>
                m.id === matchingTemp.id ? enrichedMessage : m
              )
            }
          }));
          return; // È un nostro messaggio ottimistico, non notificare
        }

        set(state => ({
          messages: {
            ...state.messages,
            [message.chat_id]: [...(state.messages[message.chat_id] || []), enrichedMessage]
          }
        }));

        // Rileggi lo stato fresco DOPO gli await per evitare stale closure
        const { data: userData } = await supabase.auth.getUser();
        const { selectedChatId, widgetState, chats } = get();
        const isOwnMessage = userData?.user?.id === message.sender_id;
        const isViewingThisChat = selectedChatId === message.chat_id && widgetState === 'expanded';

        // Se sto guardando la chat, segna il messaggio come letto (fire-and-forget)
        if (!isOwnMessage && isViewingThisChat && userData?.user?.id) {
          get().markMessageAsRead(message.id, message.chat_id);
        }

        if (!isOwnMessage && !isViewingThisChat) {
          // Suono di notifica (solo se la finestra è visibile, altrimenti c'è già la notifica browser)
          if (!document.hidden) {
            playNotificationSound();
          }

          // Controlla se l'utente è menzionato in questo messaggio
          const myName = get().organizationUsers.find(u => u.id === userData?.user?.id)?.full_name;
          const mentionCheck = /@\[([^\]]+)\]|@(\w+)/g;
          const mentionedInMsg = myName ? [...(message.content || '').matchAll(mentionCheck)]
            .some(m => (m[1] || m[2]).trim().toLowerCase() === myName.toLowerCase()) : false;

          set(state => ({
            chats: state.chats.map(chat =>
              chat.id === message.chat_id
                ? {
                    ...chat,
                    unread_count: (chat.unread_count || 0) + 1,
                    last_message: enrichedMessage,
                    has_unread_mention: chat.has_unread_mention || mentionedInMsg
                  }
                : chat
            )
          }));

          // Notifica browser se il tab non è in focus
          if (document.hidden || !document.hasFocus()) {
            const chatInfo = chats.find(c => c.id === message.chat_id);
            const chatName = chatInfo?.name || enrichedMessage.sender?.full_name || 'Chat Team';
            const senderName = enrichedMessage.sender?.full_name || 'Qualcuno';
            try {
              if (Notification.permission === 'default') {
                await Notification.requestPermission();
              }
              if (Notification.permission === 'granted') {
                const notif = new Notification(`${senderName} — ${chatName}`, {
                  body: enrichedMessage.content,
                  icon: '/favicon-48x48.png',
                  tag: `team-chat-${message.chat_id}`
                });
                notif.onclick = () => {
                  window.focus();
                  get().selectChat(message.chat_id);
                  if (get().widgetState !== 'expanded') {
                    get().setWidgetState('expanded');
                  }
                };
              }
            } catch {
              // Permesso negato o API non disponibile, ignoriamo
            }
          }
        } else {
          set(state => ({
            chats: state.chats.map(chat =>
              chat.id === message.chat_id
                ? { ...chat, last_message: enrichedMessage }
                : chat
            )
          }));
        }
      },

      updateMessageFromRealtime: (message: TeamChatMessage) => {
        const { messages } = get();
        const chatMessages = messages[message.chat_id];
        if (!chatMessages) return;

        if (message.is_deleted) {
          set(state => ({
            messages: {
              ...state.messages,
              [message.chat_id]: state.messages[message.chat_id].filter(m => m.id !== message.id)
            }
          }));
        } else {
          set(state => ({
            messages: {
              ...state.messages,
              [message.chat_id]: state.messages[message.chat_id].map(m =>
                m.id === message.id ? { ...m, ...message } : m
              )
            }
          }));
        }
      },

      fetchArchivedChats: async () => {
        try {
          const { data: userData } = await supabase.auth.getUser();
          if (!userData.user) return;

          const { data: myParticipants } = await supabase
            .from('team_chat_participants')
            .select('chat_id')
            .eq('user_id', userData.user.id);

          if (!myParticipants || myParticipants.length === 0) {
            set({ archivedChats: [] });
            return;
          }

          const chatIds = myParticipants.map(p => p.chat_id);

          const { data: archivedData } = await supabase
            .from('team_chats')
            .select(`
              *,
              participants:team_chat_participants(
                id, user_id, last_read_at,
                user:user_profiles(id, full_name, profile_image_url, avatar_url)
              )
            `)
            .in('id', chatIds)
            .eq('is_archived', true);

          set({
            archivedChats: (archivedData || []).map(chat => ({
              ...chat,
              participants: normalizeParticipants(chat.participants || [])
            }))
          });
        } catch (error) {
          console.error('[TeamChatStore] Error fetching archived chats:', error);
        }
      },

      restoreChat: async (chatId: string) => {
        try {
          const { error } = await supabase
            .from('team_chats')
            .update({ is_archived: false })
            .eq('id', chatId);

          if (error) throw error;

          // Sposta da archivedChats a chats
          const { archivedChats } = get();
          const restoredChat = archivedChats.find(c => c.id === chatId);
          if (restoredChat) {
            set(state => ({
              archivedChats: state.archivedChats.filter(c => c.id !== chatId),
              chats: [{ ...restoredChat, is_archived: false }, ...state.chats]
            }));
          }
        } catch (error) {
          console.error('[TeamChatStore] Error restoring chat:', error);
          throw error;
        }
      },

      addParticipants: async (chatId: string, userIds: string[]) => {
        try {
          const { error } = await supabase
            .from('team_chat_participants')
            .insert(userIds.map(userId => ({ chat_id: chatId, user_id: userId })));

          if (error) throw error;

          // Aggiorna la chat locale con i nuovi partecipanti
          const { data: newParticipants } = await supabase
            .from('team_chat_participants')
            .select('id, user_id, last_read_at, user:user_profiles(id, full_name, profile_image_url, avatar_url)')
            .eq('chat_id', chatId)
            .in('user_id', userIds);

          if (newParticipants) {
            const normalized = normalizeParticipants(newParticipants as unknown as TeamChatParticipant[]);
            set(state => ({
              chats: state.chats.map(chat =>
                chat.id === chatId
                  ? { ...chat, participants: [...(chat.participants || []), ...normalized] }
                  : chat
              )
            }));
          }
        } catch (error) {
          console.error('[TeamChatStore] Error adding participants:', error);
          throw error;
        }
      },

      removeParticipant: async (chatId: string, userId: string) => {
        try {
          const { error } = await supabase
            .from('team_chat_participants')
            .delete()
            .eq('chat_id', chatId)
            .eq('user_id', userId);

          if (error) throw error;

          set(state => ({
            chats: state.chats.map(chat =>
              chat.id === chatId
                ? { ...chat, participants: (chat.participants || []).filter(p => p.user_id !== userId) }
                : chat
            )
          }));
        } catch (error) {
          console.error('[TeamChatStore] Error removing participant:', error);
          throw error;
        }
      },

      setUserTyping: (chatId: string, userId: string, userName: string) => {
        const key = `${chatId}:${userId}`;

        // Cancella timeout precedente per questo utente
        const existing = typingTimeouts.get(key);
        if (existing) clearTimeout(existing);

        // Aggiungi/aggiorna utente nella lista typing
        set(state => {
          const current = state.typingUsers[chatId] || [];
          const filtered = current.filter(u => u.userId !== userId);
          return { typingUsers: { ...state.typingUsers, [chatId]: [...filtered, { userId, userName }] } };
        });

        // Auto-rimozione dopo 4 secondi
        const timeout = setTimeout(() => {
          typingTimeouts.delete(key);
          get().clearTyping(chatId, userId);
        }, 4000);
        typingTimeouts.set(key, timeout);
      },

      clearTyping: (chatId: string, userId: string) => {
        set(state => ({
          typingUsers: {
            ...state.typingUsers,
            [chatId]: (state.typingUsers[chatId] || []).filter(u => u.userId !== userId)
          }
        }));
      },

      renameChat: async (chatId: string, name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        const { error } = await supabase
          .from('team_chats')
          .update({ name: trimmed })
          .eq('id', chatId);
        if (error) throw error;
        set(state => ({
          chats: state.chats.map(c => c.id === chatId ? { ...c, name: trimmed } : c)
        }));
      },

      leaveChat: async (chatId: string) => {
        try {
          const { data: userData } = await supabase.auth.getUser();
          if (!userData.user) throw new Error('Not authenticated');

          const { error } = await supabase
            .from('team_chat_participants')
            .delete()
            .eq('chat_id', chatId)
            .eq('user_id', userData.user.id);

          if (error) throw error;

          set(state => ({
            chats: state.chats.filter(c => c.id !== chatId),
            selectedChatId: state.selectedChatId === chatId ? null : state.selectedChatId
          }));
        } catch (error) {
          console.error('[TeamChatStore] Error leaving chat:', error);
          throw error;
        }
      },

      archiveChat: async (chatId: string) => {
        try {
          const { error } = await supabase
            .from('team_chats')
            .update({ is_archived: true })
            .eq('id', chatId);

          if (error) throw error;

          set(state => ({
            chats: state.chats.filter(c => c.id !== chatId),
            selectedChatId: state.selectedChatId === chatId ? null : state.selectedChatId
          }));
        } catch (error) {
          console.error('[TeamChatStore] Error archiving chat:', error);
          throw error;
        }
      },

      // ── Enhanced features ──────────────────────────────────────────────────

      searchMessages: async (chatId: string, query: string) => {
        const { data, error } = await supabase
          .from('team_chat_messages')
          .select('id, content, created_at, sender:user_profiles!sender_id(full_name)')
          .eq('chat_id', chatId)
          .eq('is_deleted', false)
          .textSearch('content', query, { type: 'websearch', config: 'italian' })
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) {
          // Fallback: ilike search se full-text non disponibile
          const { data: fallbackData } = await supabase
            .from('team_chat_messages')
            .select('id, content, created_at, sender:user_profiles!sender_id(full_name)')
            .eq('chat_id', chatId)
            .eq('is_deleted', false)
            .ilike('content', `%${query}%`)
            .order('created_at', { ascending: false })
            .limit(50);
          return (fallbackData || []).map((m: any) => ({
            id: m.id,
            content: m.content,
            sender_name: m.sender?.full_name || 'Utente',
            created_at: m.created_at,
          }));
        }

        return (data || []).map((m: any) => ({
          id: m.id,
          content: m.content,
          sender_name: m.sender?.full_name || 'Utente',
          created_at: m.created_at,
        }));
      },

      searchAllChats: async (query: string) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return [];

        // Get user's chat IDs
        const { data: myParticipants } = await supabase
          .from('team_chat_participants')
          .select('chat_id')
          .eq('user_id', userData.user.id);

        if (!myParticipants?.length) return [];
        const chatIds = myParticipants.map(p => p.chat_id);

        const { data, error } = await supabase
          .from('team_chat_messages')
          .select('id, chat_id, content, created_at, sender:user_profiles!sender_id(full_name)')
          .in('chat_id', chatIds)
          .eq('is_deleted', false)
          .ilike('content', `%${query}%`)
          .order('created_at', { ascending: false })
          .limit(30);

        if (error) return [];

        const { chats } = get();
        return (data || []).map((m: any) => {
          const chat = chats.find(c => c.id === m.chat_id);
          const chatName = chat?.name || chat?.participants?.find(p => p.user_id !== userData.user!.id)?.user?.full_name || 'Chat';
          return {
            id: m.id,
            chat_id: m.chat_id,
            content: m.content,
            sender_name: m.sender?.full_name || 'Utente',
            created_at: m.created_at,
            chat_name: chatName,
          };
        });
      },

      forwardMessage: async (messageId: string, fromChatId: string, toChatId: string) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error('Not authenticated');

        // Fetch il messaggio originale
        const msgs = get().messages[fromChatId] || [];
        const original = msgs.find(m => m.id === messageId);
        if (!original) throw new Error('Messaggio non trovato');

        // Crea un nuovo messaggio nella chat destinataria con riferimento all'originale
        const forwardContent = original.content || '';
        const { data: newMsg, error } = await supabase
          .from('team_chat_messages')
          .insert({
            chat_id: toChatId,
            sender_id: userData.user.id,
            content: forwardContent,
            message_type: original.message_type || 'text',
            attachments: original.attachments || [],
            forwarded_from_id: messageId,
          })
          .select('*, sender:user_profiles!sender_id(id, full_name, profile_image_url, avatar_url)')
          .single();

        if (error) throw error;

        if (newMsg) {
          const s = (newMsg as any).sender;
          if (s) s.profile_image_url = s.avatar_url || s.profile_image_url || null;
          const enrichedMsg = { ...newMsg, forwarded_from: original } as unknown as TeamChatMessage;

          set(state => ({
            messages: {
              ...state.messages,
              [toChatId]: [...(state.messages[toChatId] || []), enrichedMsg]
            },
            chats: state.chats.map(chat =>
              chat.id === toChatId ? { ...chat, last_message: enrichedMsg } : chat
            )
          }));

          // Notifica
          const chat = get().chats.find(c => c.id === toChatId);
          if (chat) {
            const senderName = (newMsg as any).sender?.full_name || 'Utente';
            sendTeamChatNotification(chat, userData.user.id, senderName, forwardContent, newMsg.id);
          }
        }
      },

      togglePinMessage: async (messageId: string, chatId: string) => {
        const msgs = get().messages[chatId] || [];
        const msg = msgs.find(m => m.id === messageId);
        if (!msg) return;

        const newPinned = !(msg as any).is_pinned;
        const { error } = await supabase
          .from('team_chat_messages')
          .update({ is_pinned: newPinned })
          .eq('id', messageId);

        if (error) throw error;

        set(state => ({
          messages: {
            ...state.messages,
            [chatId]: (state.messages[chatId] || []).map(m =>
              m.id === messageId ? { ...m, is_pinned: newPinned } as any : m
            )
          }
        }));
      },

      getPinnedMessages: async (chatId: string) => {
        const { data, error } = await buildMessagesQuery(chatId)
          .eq('is_pinned', true)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) return [];
        return normalizeMessages((data || []).reverse());
      },

      toggleMuteChat: async (chatId: string) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;

        // Leggi stato corrente
        const { data: participant } = await supabase
          .from('team_chat_participants')
          .select('is_muted')
          .eq('chat_id', chatId)
          .eq('user_id', userData.user.id)
          .single();

        const newMuted = !(participant?.is_muted);
        const { error } = await supabase
          .from('team_chat_participants')
          .update({ is_muted: newMuted })
          .eq('chat_id', chatId)
          .eq('user_id', userData.user.id);

        if (error) throw error;

        // Aggiorna localmente
        set(state => ({
          chats: state.chats.map(c =>
            c.id === chatId ? { ...c, is_muted: newMuted } as any : c
          )
        }));
      },

      markMessageAsRead: async (messageId: string, chatId: string) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;

        // Aggiorna read_by nel messaggio (append all'array se non presente)
        const { error: rpcError } = await supabase.rpc('team_chat_mark_message_read', {
          p_message_id: messageId,
          p_user_id: userData.user.id,
        });

        if (rpcError) {
          // Fallback: update diretto se RPC non esiste
          const { data } = await supabase
            .from('team_chat_messages')
            .select('read_by')
            .eq('id', messageId)
            .single();
          const current: string[] = (data as any)?.read_by || [];
          if (!current.includes(userData.user!.id)) {
            await supabase.from('team_chat_messages')
              .update({ read_by: [...current, userData.user!.id] })
              .eq('id', messageId);
          }
        }
      },

      getMessageReadBy: async (messageId: string) => {
        const { data } = await supabase
          .from('team_chat_messages')
          .select('read_by')
          .eq('id', messageId)
          .single();

        const readByIds: string[] = (data as any)?.read_by || [];
        if (readByIds.length === 0) return [];

        const { data: users } = await supabase
          .from('user_profiles')
          .select('id, full_name, profile_image_url, avatar_url')
          .in('id', readByIds);

        return (users || []).map(u => ({
          user_id: u.id,
          full_name: u.full_name,
          profile_image_url: (u as any).avatar_url || u.profile_image_url || null,
        }));
      },

      // ── Saved messages ──────────────────────────────────────────────────────
      fetchSavedMessageIds: async () => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;
        const { data } = await supabase
          .from('team_chat_saved_messages')
          .select('message_id')
          .eq('user_id', userData.user.id);
        set({ savedMessageIds: new Set((data || []).map((d: any) => d.message_id)) });
      },

      saveMessage: async (messageId: string, chatId: string) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;
        // Optimistic update
        set(state => {
          const next = new Set(state.savedMessageIds);
          next.add(messageId);
          return { savedMessageIds: next };
        });
        const { data: orgData } = await supabase
          .from('team_chat_participants')
          .select('chat:team_chats!chat_id(organization_id)')
          .eq('chat_id', chatId)
          .eq('user_id', userData.user.id)
          .single();
        const orgId = (orgData as any)?.chat?.organization_id;
        if (!orgId) return;
        const { error } = await supabase.from('team_chat_saved_messages').insert({
          user_id: userData.user.id,
          message_id: messageId,
          chat_id: chatId,
          organization_id: orgId,
        });
        if (error) {
          // Revert on error
          set(state => {
            const next = new Set(state.savedMessageIds);
            next.delete(messageId);
            return { savedMessageIds: next };
          });
        }
      },

      unsaveMessage: async (messageId: string) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;
        set(state => {
          const next = new Set(state.savedMessageIds);
          next.delete(messageId);
          return { savedMessageIds: next };
        });
        await supabase.from('team_chat_saved_messages')
          .delete()
          .eq('user_id', userData.user.id)
          .eq('message_id', messageId);
      },

      getSavedMessages: async () => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return [];
        const { data: saved } = await supabase
          .from('team_chat_saved_messages')
          .select('message_id, chat_id, chat:team_chats!chat_id(name)')
          .eq('user_id', userData.user.id)
          .order('created_at', { ascending: false })
          .limit(50);
        if (!saved || saved.length === 0) return [];

        const messageIds = saved.map((s: any) => s.message_id);
        const { data: allMsgs } = await supabase
          .from('team_chat_messages')
          .select(`*, sender:user_profiles!sender_id(id, full_name, profile_image_url, avatar_url)`)
          .in('id', messageIds);

        const chatNameMap = new Map<string, string>();
        saved.forEach((s: any) => {
          if (s.chat?.name) chatNameMap.set(s.chat_id, s.chat.name);
        });

        return normalizeMessages(allMsgs || []).map(m => ({
          ...m,
          chat_name: chatNameMap.get(m.chat_id) || undefined,
        }));
      },

      // ── Scheduled messages ──────────────────────────────────────────────────
      scheduleMessage: async (chatId, content, scheduledAt, replyToId, attachments, messageType) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;
        // Ottieni organization_id
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();
        if (!profile?.organization_id) return;

        const { data, error } = await supabase
          .from('team_chat_scheduled_messages')
          .insert({
            organization_id: profile.organization_id,
            chat_id: chatId,
            sender_id: userData.user.id,
            content,
            message_type: messageType || 'text',
            attachments: attachments || [],
            reply_to_id: replyToId || null,
            scheduled_at: scheduledAt.toISOString(),
          })
          .select()
          .single();

        if (error) throw error;
        if (data) {
          set(state => ({ scheduledMessages: [...state.scheduledMessages, data] }));
        }
      },

      getScheduledMessages: async (chatId) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return [];
        let query = supabase
          .from('team_chat_scheduled_messages')
          .select('*')
          .eq('sender_id', userData.user.id)
          .eq('status', 'scheduled')
          .order('scheduled_at', { ascending: true });
        if (chatId) query = query.eq('chat_id', chatId);
        const { data } = await query;
        const msgs = (data || []) as ScheduledMessage[];
        set({ scheduledMessages: msgs });
        return msgs;
      },

      cancelScheduledMessage: async (messageId) => {
        await supabase
          .from('team_chat_scheduled_messages')
          .update({ status: 'cancelled' })
          .eq('id', messageId);
        set(state => ({
          scheduledMessages: state.scheduledMessages.filter(m => m.id !== messageId),
        }));
      },

      // ── Threads ────────────────────────────────────────────────────────────────
      fetchThreadMessages: async (chatId, rootMessageId) => {
        const selectStr = `*, sender:user_profiles!sender_id(id, full_name, profile_image_url, avatar_url), reactions:team_chat_message_reactions(emoji, user_id)`;
        const { data } = await supabase
          .from('team_chat_messages')
          .select(selectStr)
          .eq('chat_id', chatId)
          .eq('reply_to_id', rootMessageId)
          .eq('is_deleted', false)
          .order('created_at', { ascending: true });
        return normalizeMessages(data || []);
      },

      sendThreadMessage: async (chatId, rootMessageId, content, attachments, messageType) => {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) return;

        // Inserisci il messaggio nel thread
        const { error } = await supabase
          .from('team_chat_messages')
          .insert({
            chat_id: chatId,
            sender_id: userData.user.id,
            content,
            message_type: messageType || 'text',
            attachments: attachments || [],
            reply_to_id: rootMessageId,
          });
        if (error) throw error;

        // Aggiorna contatori sul messaggio root
        // Usiamo una query per contare i reply attuali
        const { count } = await supabase
          .from('team_chat_messages')
          .select('id', { count: 'exact', head: true })
          .eq('reply_to_id', rootMessageId)
          .eq('is_deleted', false);

        await supabase
          .from('team_chat_messages')
          .update({
            thread_message_count: count || 0,
            thread_last_reply_at: new Date().toISOString(),
          })
          .eq('id', rootMessageId);

        // Aggiorna la cache locale del messaggio root
        set(state => {
          const chatMsgs = state.messages[chatId];
          if (!chatMsgs) return state;
          return {
            messages: {
              ...state.messages,
              [chatId]: chatMsgs.map(m =>
                m.id === rootMessageId
                  ? { ...m, thread_message_count: count || 0, thread_last_reply_at: new Date().toISOString() }
                  : m
              ),
            },
          };
        });
      },

      reset: () => {
        typingTimeouts.forEach(t => clearTimeout(t));
        typingTimeouts.clear();
        set({
          chats: [],
          archivedChats: [],
          selectedChatId: null,
          messages: {},
          messagesCacheOrder: [],
          hasMoreMessages: {},
          loadingOlderMessages: false,
          loading: false,
          loadingMessages: false,
          error: null,
          widgetState: 'minimized',
          replyingTo: null,
          typingUsers: {},
          savedMessageIds: new Set<string>(),
          scheduledMessages: [],
        });
      }
    })
);
